import { useEffect, useState } from 'react';
import { WithRouterProps } from 'react-router';

import { ApolloClient } from '@apollo/client';
import { SearchRemixer_QueryInfoFieldsFragment } from '@reverbdotcom/commons/src/gql/graphql';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { IUrl } from '@reverbdotcom/commons/src/url_context';

import {
  v0Input,
  MARKETPLACE_REMIXER_SEARCH,
  SearchRemixer_MarketplaceListingsSearch,
} from './query_helpers';

function cacheKey(searchUrl) {
  const input = v0Input(searchUrl);
  return {
    query: MARKETPLACE_REMIXER_SEARCH,
    variables: {
      input: {
        searchRemixerV0Input: input,
      },
    },
  };
}

function isCached(searchUrl, apolloClient: ApolloClient<any>) {
  try {
    return !!apolloClient.cache.readQuery(cacheKey(searchUrl));
  } catch {
    return false;
  }
}

/*  When autodirected searches return, the frontend persists autodirect params to the url
    to reflect the autoapplied filters (ex. ?query=fender becomes ?query=fender&make=fender).
    Before the url updates, this function caches search results with the autodirect params to tell apollo:
    "hey! cache `{ query: "fender", make: "fender" }` with the same results as `{ query: "fender" }`".
    Otherwise, apollo will dumbly think it's a new query and fire an extra graphql request (which we don't want!).
*/
function cacheQueries({ apolloClient, currentUrl, adjustedUrl, marketplaceListingsSearch }: {
  apolloClient: ApolloClient<any>, currentUrl: URL, adjustedUrl: URL, marketplaceListingsSearch: SearchRemixer_MarketplaceListingsSearch
}) {
  if (!isCached(currentUrl, apolloClient)) {
    apolloClient.cache.writeQuery({ ...cacheKey(currentUrl), data: { marketplaceListingsSearch } });
  }

  if (!isCached(adjustedUrl, apolloClient)) {
    apolloClient.cache.writeQuery({ ...cacheKey(adjustedUrl), data: { marketplaceListingsSearch } });
  }
}

export function applyAdjustedUrl({ apolloClient, adjustedSearchUrl, marketplaceListingsSearch, routerProps }: {
  apolloClient: ApolloClient<any>,
  adjustedSearchUrl: string,
  marketplaceListingsSearch: SearchRemixer_MarketplaceListingsSearch,
  routerProps: WithRouterProps
}) {
  if (!adjustedSearchUrl) {
    return;
  }
  const { location } = routerProps;
  const currentUrl = new URL(`${location.pathname}${location.search}`, window.location.origin);
  const adjustedUrl = new URL(adjustedSearchUrl, window.location.origin);
  if (currentUrl === adjustedUrl) {
    return;
  }
  cacheQueries({ currentUrl, adjustedUrl, apolloClient, marketplaceListingsSearch });
  routerProps.router.replace(adjustedSearchUrl);
}

interface UseSearchRedirectsParams {
  apolloClient: ApolloClient<any>;
  marketplaceListingsSearch: SearchRemixer_MarketplaceListingsSearch;
  url: IUrl;
  routerProps: WithRouterProps;
}

export function useSearchRedirects({
  apolloClient,
  marketplaceListingsSearch,
  url,
  routerProps,
}: UseSearchRedirectsParams) {
  const [autodirectApplied, setAutodirectApplied] = useState(false);
  const [spellcheckApplied, setSpellcheckApplied] = useState(false);

  const queryInfo: SearchRemixer_QueryInfoFieldsFragment = marketplaceListingsSearch?.queryInfo;

  const shouldApplyAutodirect = (
    marketplaceListingsSearch?.adjustedSearchUrl &&
    url.query?.skip_autodirects !== 'true'
  );

  // Only apply spellcheck if autodirects are not being applied,
  // since autodirects will already have the corrected query if applicable.
  const shouldApplySpellcheck = (
    !shouldApplyAutodirect &&
    queryInfo?.spellCorrected &&
    url.query?.skip_autocorrect !== 'true'
  );

  const spellcheckUrl = Paths.marketplaceV2.expand({
    ...routerProps.location.query,
    query: queryInfo?.executedQuery,
  });

  useEffect(() => {
    if (shouldApplyAutodirect && !autodirectApplied) {
      applyAdjustedUrl({
        apolloClient,
        adjustedSearchUrl: marketplaceListingsSearch.adjustedSearchUrl,
        marketplaceListingsSearch,
        routerProps,
      });
      setAutodirectApplied(true);
    } else if (shouldApplySpellcheck && !spellcheckApplied) {
      applyAdjustedUrl({
        apolloClient,
        adjustedSearchUrl: spellcheckUrl,
        marketplaceListingsSearch,
        routerProps,
      });
      setSpellcheckApplied(true);
    }
  }, [
    shouldApplyAutodirect,
    autodirectApplied,
    applyAdjustedUrl,
    apolloClient,
    marketplaceListingsSearch,
    routerProps,
    setAutodirectApplied,
    shouldApplySpellcheck,
    spellcheckApplied,
    spellcheckUrl,
    setSpellcheckApplied,
  ]);
}
